import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";

const DEFAULT_COLORS = ["#2563eb", "#2563eb", "#34d399", "#c084fc", "#fb7185", "#f97316", "#14b8a6", "#eab308", "#0ea5e9", "#0f766e", "#7c3aed"];

export default function AdminCarriers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", product_types: "", color: "#2563eb", notes: "" });

  const load = () => api.get("/carriers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", product_types: "", color: DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)], notes: "" });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, product_types: (c.product_types || []).join(", "), color: c.color || "#2563eb", notes: c.notes || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      product_types: form.product_types.split(",").map((s) => s.trim()).filter(Boolean),
      color: form.color,
      notes: form.notes,
    };
    try {
      if (editing) await api.patch(`/carriers/${editing.id}`, payload);
      else await api.post("/carriers", payload);
      toast.success(editing ? "Carrier updated" : "Carrier added");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await api.delete(`/carriers/${c.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Admin</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-1">Carriers</h1>
          <p className="text-zinc-600 mt-2">Your full line-up of insurance & annuity carriers.</p>
        </div>
        <Button onClick={openNew} className="rounded-none bg-[#09090b] text-white border-2 border-black hover:bg-black shadow-[4px_4px_0_0_#2563eb] font-bold uppercase tracking-widest text-xs h-11" data-testid="add-carrier-btn">
          <Plus size={16} weight="bold" className="mr-1" /> Add Carrier
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((c) => (
          <div key={c.id} className="brut bg-white p-5" data-testid={`carrier-${c.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="inline-block border-2 border-black px-3 py-1.5 font-bold text-sm" style={{ background: c.color || "#2563eb" }}>
                {c.name}
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(c)} className="border-2 border-black p-2 hover:bg-[#2563eb]" data-testid={`edit-carrier-${c.id}`}><PencilSimple size={14} weight="bold" /></button>
                <button onClick={() => del(c)} className="border-2 border-black p-2 hover:bg-[#fb7185]" data-testid={`delete-carrier-${c.id}`}><Trash size={14} weight="bold" /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(c.product_types || []).map((p) => (
                <span key={p} className="text-[10px] font-bold uppercase tracking-widest border-2 border-black px-1.5 py-0.5 bg-[#f4f4f5]">{p}</span>
              ))}
            </div>
            {c.notes && <div className="mt-3 text-xs text-zinc-500">{c.notes}</div>}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#09090b]">
          <DialogHeader><DialogTitle className="font-display font-black tracking-tight">{editing ? "Edit Carrier" : "Add Carrier"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label className="font-bold uppercase text-xs tracking-widest">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0" data-testid="carrier-name" />
            </div>
            <div>
              <Label className="font-bold uppercase text-xs tracking-widest">Product Types (comma-separated)</Label>
              <Input value={form.product_types} onChange={(e) => setForm({ ...form, product_types: e.target.value })} placeholder="IUL, Annuity, Term" className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0" />
            </div>
            <div>
              <Label className="font-bold uppercase text-xs tracking-widest">Color</Label>
              <div className="flex gap-2 flex-wrap mt-2">
                {DEFAULT_COLORS.map((col) => (
                  <button type="button" key={col} onClick={() => setForm({ ...form, color: col })} className="w-8 h-8 border-2 border-black" style={{ background: col, outline: form.color === col ? "3px solid #09090b" : "none", outlineOffset: 2 }} />
                ))}
              </div>
            </div>
            <div>
              <Label className="font-bold uppercase text-xs tracking-widest">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 border-2 border-black rounded-none focus-visible:ring-0" />
            </div>
            <Button type="submit" className="w-full h-11 rounded-none bg-[#2563eb] text-white border-2 border-black shadow-[4px_4px_0_0_#09090b] hover:shadow-[6px_6px_0_0_#09090b] hover:-translate-y-0.5 font-bold uppercase tracking-widest" data-testid="save-carrier-btn">Save</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
